<script setup lang="ts">
// A DM's people, in the members panel's slot (HRTH-151): online first. In a group, anyone
// may add people and whoever started it may remove them. In a 1:1, adding people starts a
// new group, so the 1:1's history stays between the two of you. Its name and leaving are
// in its settings (ConversationSettingsModal).
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import type { ConversationInfo } from '../api/types'
import { describeError } from '../api/http'
import { displayName, dmTitle } from '../lib/format'
import { useSession } from '../stores/session'
import Avatar from './Avatar.vue'
import Modal from './Modal.vue'
import PeoplePicker from './PeoplePicker.vue'

const MAX_MEMBERS = 16

const props = defineProps<{ conv: ConversationInfo }>()
const session = useSession()
const router = useRouter()

const group = computed(() => !!props.conv.group)
const iStarted = computed(() => props.conv.created_by === session.me)
const members = computed(() =>
  [...props.conv.members].sort(
    (a, b) =>
      Number(session.online.has(b)) - Number(session.online.has(a)) ||
      displayName(session.profiles, a).localeCompare(displayName(session.profiles, b)),
  ),
)
const title = computed(() => dmTitle(session.profiles, props.conv.members, session.me, props.conv.name))
const memberTitle = computed(() => dmTitle(session.profiles, props.conv.members, session.me))
/** Hosted on another server: its people are managed there. */
const editable = computed(() => !props.conv.host)

const adding = ref(false)
const busy = ref(false)

async function run(fn: () => Promise<unknown>): Promise<void> {
  busy.value = true
  try {
    await fn()
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}

const add = (users: string[]) =>
  run(async () => {
    if (group.value) {
      await session.addDmMembers(props.conv.id, users)
      adding.value = false
      return
    }
    // From a 1:1: a new group with the two of you and them.
    const others = props.conv.members.filter((m) => m !== session.me)
    const id = await session.createDm([...others, ...users])
    adding.value = false
    await router.push({ name: 'dm', params: { convId: id } })
  })

function remove(user: string): void {
  if (!confirm(`Remove ${displayName(session.profiles, user)} from “${title.value}”? They keep what they've already seen.`)) return
  void run(() => session.removeDmMember(props.conv.id, user))
}

/** In a group, room left; from a 1:1, a new group of at most 16. */
const room = computed(() => MAX_MEMBERS - props.conv.members.length)
const groupOf = (n: number) => (n ? `Group of ${props.conv.members.length + n}` : '')
</script>

<template>
  <aside class="scroll-thin w-60 shrink-0 overflow-y-auto border-l border-border bg-bg-secondary py-4" aria-label="People" data-testid="dm-members">
    <div class="flex items-center justify-between px-4 pb-2">
      <h3 class="label mb-0">People / {{ String(conv.members.length).padStart(2, '0') }}</h3>
      <button v-if="editable && room > 0" class="btn-ghost h-7 px-2" data-testid="dm-add" @click="adding = true">Add</button>
    </div>
    <div v-for="m in members" :key="m" class="flex items-center gap-2.5 px-4 py-1.5" :data-testid="`dm-member-${displayName(session.profiles, m)}`">
      <Avatar :id="m" :name="displayName(session.profiles, m)" :size="24" presence />
      <div class="min-w-0 flex-1">
        <div class="truncate text-sm text-text-secondary">
          {{ displayName(session.profiles, m) }}<span v-if="m === session.me" class="text-text-muted"> (you)</span>
        </div>
        <div v-if="group && m === conv.created_by" class="mono text-text-muted">started the group</div>
      </div>
      <button
        v-if="editable && group && iStarted && m !== session.me"
        class="btn-ghost h-7 px-2 text-error hover:border-signal hover:text-error"
        :disabled="busy"
        :data-testid="`dm-remove-${displayName(session.profiles, m)}`"
        @click="remove(m)"
      >
        Remove
      </button>
    </div>

    <Modal v-if="adding" title="Add people" @close="adding = false">
      <div data-testid="dm-add-picker">
        <p v-if="!group" class="mb-3 text-sm text-text-muted">
          This starts a new group with you, {{ memberTitle }} and whoever you pick. Your 1:1 stays as it is.
        </p>
        <p v-else class="mb-3 text-sm text-text-muted">They'll see messages from when they join, not before.</p>
        <PeoplePicker
          :exclude="conv.members"
          :max="room"
          :busy="busy"
          :submit-label="group ? 'Add' : 'Start group'"
          :summary="groupOf"
          @pick="add"
        />
      </div>
    </Modal>
  </aside>
</template>
